// ==========================================
// SAPUNA HEADER - COMPLETE FUNCTIONALITY
// Cart drawer, mobile menu, add to cart, and Shopify sync
// ==========================================

(function() {
  'use strict';
  
  // ==========================================
  // STATE VARIABLES
  // ==========================================
  let cartItems = [];
  let isSyncing = false;
  
  // ==========================================
  // HELPER FUNCTIONS
  // ==========================================
  function escapeHtml(str) {
    if (!str) return '';
    return String(str).replace(/[&<>"]/g, function(m) {
      if (m === '&') return '&amp;';
      if (m === '<') return '&lt;';
      if (m === '>') return '&gt;';
      if (m === '"') return '&quot;';
      return m;
    });
  }
  
  function formatMoney(value) {
    const number = parseFloat(value) || 0;
    return `$${number.toFixed(2)}`;
  }
  
  // ==========================================
  // SHOPIFY CART LOADING
  // ==========================================
  async function loadShopifyCart() {
    if (isSyncing) return;
    isSyncing = true;
    
    try {
      const response = await fetch('/cart.js', {
        method: 'GET',
        headers: {
          'Accept': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to load cart');
      }
      
      const cart = await response.json();
      
      cartItems = (cart.items || []).map(item => ({
        title: item.product_title || 'Product',
        variant: item.variant_title && item.variant_title !== 'Default Title' ? item.variant_title : '',
        price: (item.final_price || item.price || 0) / 100,
        variantId: String(item.variant_id),
        quantity: parseInt(item.quantity, 10) || 1,
        image: item.image || (item.featured_image && item.featured_image.url) || '',
        lineKey: item.key || '',
        originalPrice: (item.original_price || 0) / 100,
        url: item.url || ''
      }));
      
      updateCartUI(cart);
      updateCartCount(cart);
    } catch (error) {
      console.error('Error loading Shopify cart:', error);
    } finally {
      isSyncing = false;
    }
  }
  
  function updateCartCount(cartData) {
    let totalQty = 0;
    
    if (cartData && typeof cartData.item_count !== 'undefined') {
      totalQty = cartData.item_count;
    } else {
      totalQty = cartItems.reduce((sum, i) => sum + (parseInt(i.quantity, 10) || 0), 0);
    }
    
    document.querySelectorAll('.cart-count').forEach(el => {
      el.textContent = totalQty;
    });
  }
  
  function updateCartUI(cartData = null) {
    const container = document.getElementById('cartContent');
    const totalSpan = document.getElementById('cartTotalAmount');
    
    if (!container) return;
    
    if (!cartItems.length) {
      container.innerHTML = '<div class="cart-empty-message">Your cart is empty<br><small>Add some products to get started!</small></div>';
      if (totalSpan) totalSpan.textContent = '$0.00';
      return;
    }
    
    let html = '';
    let total = 0;
    
    cartItems.forEach((item, idx) => {
      const price = parseFloat(item.price) || 0;
      const qty = parseInt(item.quantity, 10) || 1;
      const itemTotal = price * qty;
      total += itemTotal;
      
      html += `
        <div class="cart-item" data-cart-index="${idx}" data-variant-id="${item.variantId}">
          <div class="cart-item-header">
            <div style="display:flex; gap:12px; align-items:flex-start;">
              ${item.image ? `<img src="${item.image}" alt="${escapeHtml(item.title)}" style="width:60px;height:60px;object-fit:cover;border-radius:10px;border:1px solid var(--sapuna-border);">` : ''}
              <div>
                <div class="cart-item-title">${escapeHtml(item.title)}</div>
                ${item.variant ? `<div style="font-size:13px;color:var(--sapuna-secondary-text);margin-bottom:4px;">${escapeHtml(item.variant)}</div>` : ''}
                <div class="cart-item-price">${formatMoney(price)}</div>
              </div>
            </div>
            <button class="trash-icon" onclick="window.removeCartItem(${idx})" aria-label="Remove item">🗑️</button>
          </div>
          
          <div class="quantity-controls">
            <button class="quantity-btn" onclick="window.updateQty(${idx}, ${qty - 1})" aria-label="Decrease quantity">−</button>
            <span style="min-width:30px;text-align:center;">${qty}</span>
            <button class="quantity-btn" onclick="window.updateQty(${idx}, ${qty + 1})" aria-label="Increase quantity">+</button>
          </div>
          
          <div style="font-size:13px; margin-top:5px; color:var(--sapuna-secondary-text);">
            Item total: ${formatMoney(itemTotal)}
          </div>
        </div>
      `;
    });
    
    container.innerHTML = html;
    
    if (totalSpan) {
      if (cartData && typeof cartData.total_price !== 'undefined') {
        totalSpan.textContent = `$${(cartData.total_price / 100).toFixed(2)}`;
      } else {
        totalSpan.textContent = formatMoney(total);
      }
    }
  }
  
  // ==========================================
  // SHOPIFY CART ACTIONS
  // ==========================================
  async function addToCart(productData) {
    if (!productData || !productData.variantId) {
      console.error('Missing variantId in productData');
      return false;
    }
    
    const quantity = parseInt(productData.quantity, 10) || 1;
    
    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          id: productData.variantId,
          quantity: quantity
        })
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to add item to cart');
      }
      
      await loadShopifyCart();
      openCartDrawer();
      
      // Dispatch events for other components
      document.dispatchEvent(new CustomEvent('cart:refresh'));
      document.dispatchEvent(new CustomEvent('cart:updated'));
      
      return true;
    } catch (error) {
      console.error('Add to cart failed:', error);
      alert(error.message || 'Sorry, this product could not be added to cart.');
      return false;
    }
  }
  
  async function updateQty(index, newQty) {
    const item = cartItems[index];
    if (!item) return;
    
    const qty = Math.max(0, parseInt(newQty, 10) || 0);
    
    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          id: item.variantId,
          quantity: qty
        })
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to update cart');
      }
      
      const updatedCart = await response.json();
      
      cartItems = (updatedCart.items || []).map(item => ({
        title: item.product_title || 'Product',
        variant: item.variant_title && item.variant_title !== 'Default Title' ? item.variant_title : '',
        price: (item.final_price || item.price || 0) / 100,
        variantId: String(item.variant_id),
        quantity: parseInt(item.quantity, 10) || 1,
        image: item.image || (item.featured_image && item.featured_image.url) || '',
        lineKey: item.key || '',
        originalPrice: (item.original_price || 0) / 100,
        url: item.url || ''
      }));
      
      updateCartUI(updatedCart);
      updateCartCount(updatedCart);
      
      document.dispatchEvent(new CustomEvent('cart:refresh'));
      document.dispatchEvent(new CustomEvent('cart:updated'));
    } catch (error) {
      console.error('Update quantity failed:', error);
    }
  }
  
  async function removeCartItem(index) {
    await updateQty(index, 0);
  }
  
  function proceedCheckout() {
    if (!cartItems.length) return;
    window.location.href = '/checkout';
  }
  
  // ==========================================
  // CART DRAWER CONTROLS
  // ==========================================
  function openCartDrawer() {
    const drawer = document.getElementById('cartDrawer');
    const overlay = document.getElementById('cartOverlay');
    
    if (drawer) drawer.classList.add('active');
    if (overlay) overlay.classList.add('active');
    
    document.body.style.overflow = 'hidden';
  }
  
  function closeCartDrawer() {
    const drawer = document.getElementById('cartDrawer');
    const overlay = document.getElementById('cartOverlay');
    
    if (drawer) drawer.classList.remove('active');
    if (overlay) overlay.classList.remove('active');
    
    document.body.style.overflow = '';
  }
  
  // ==========================================
  // MOBILE MENU FUNCTIONS
  // ==========================================
  function toggleMobileMenu() {
    const menu = document.getElementById('mobileMenu');
    const btn = document.getElementById('mobileToggleBtn');
    
    if (!menu || !btn) return;
    
    menu.classList.toggle('active');
    btn.classList.toggle('active');
    document.body.style.overflow = menu.classList.contains('active') ? 'hidden' : '';
  }
  
  function closeMobileMenu() {
    const menu = document.getElementById('mobileMenu');
    const btn = document.getElementById('mobileToggleBtn');
    
    if (menu) menu.classList.remove('active');
    if (btn) btn.classList.remove('active');
    document.body.style.overflow = '';
  }
  
  function initMobileCollapsibles() {
    const headers = document.querySelectorAll('.mobile-menu-header');
    
    headers.forEach(header => {
      header.addEventListener('click', (e) => {
        e.stopPropagation();
        
        const targetId = header.getAttribute('data-collapse');
        const submenu = document.getElementById(targetId);
        
        if (submenu) {
          submenu.classList.toggle('open');
          
          const arrow = header.querySelector('.dropdown-arrow');
          if (arrow) {
            arrow.style.transform = submenu.classList.contains('open')
              ? 'rotate(180deg)'
              : 'rotate(0deg)';
          }
        }
      });
    });
  }
  
  // ==========================================
  // ACTIVE NAVIGATION STATE
  // ==========================================
  function setActiveNavState() {
    const currentPath = window.location.pathname;
    
    // Desktop nav elements
    const navHome = document.getElementById('navHome');
    const navAbout = document.getElementById('navAbout');
    const navContact = document.getElementById('navContact');
    const navShop = document.getElementById('navShop');
    
    // Mobile nav elements
    const mobileHome = document.getElementById('mobileHome');
    const mobileAbout = document.getElementById('mobileAbout');
    const mobileContact = document.getElementById('mobileContact');
    
    // Remove active class from all first
    const allNavLinks = document.querySelectorAll('.main-nav a, .simple-mobile-link, .nav-dropdown > a');
    allNavLinks.forEach(link => {
      link.classList.remove('active');
    });
    
    // Set active based on current path
    if (currentPath === '/' || currentPath === '/home' || currentPath === '') {
      if (navHome) navHome.classList.add('active');
      if (mobileHome) mobileHome.classList.add('active');
    } else if (currentPath.includes('/pages/about')) {
      if (navAbout) navAbout.classList.add('active');
      if (mobileAbout) mobileAbout.classList.add('active');
    } else if (currentPath.includes('/pages/contact')) {
      if (navContact) navContact.classList.add('active');
      if (mobileContact) mobileContact.classList.add('active');
    } else if (currentPath.includes('/collections')) {
      if (navShop) navShop.classList.add('active');
    }
  }
  
  // ==========================================
  // EXPOSE FUNCTIONS GLOBALLY
  // ==========================================
  // Main cart functions - available to ALL pages
  window.addToCart = addToCart;
  window.updateQty = updateQty;
  window.removeCartItem = removeCartItem;
  window.openCartDrawer = openCartDrawer;
  window.closeCartDrawer = closeCartDrawer;
  
  // Optional alias for compatibility
  window.addProductToCart = addToCart;
  
  // ==========================================
  // INITIALIZATION
  // ==========================================
  function init() {
    // Load cart data
    loadShopifyCart();
    
    // Initialize mobile collapsibles
    initMobileCollapsibles();
    
    // Set active navigation state
    setActiveNavState();
    
    // Get DOM elements
    const mobileBtn = document.getElementById('mobileToggleBtn');
    const mobileMenu = document.getElementById('mobileMenu');
    const cartBtn = document.getElementById('cartButton');
    const closeCartBtn = document.getElementById('closeCartBtn');
    const overlay = document.getElementById('cartOverlay');
    const checkoutBtn = document.getElementById('checkoutBtn');
    
    // Mobile menu toggle
    if (mobileBtn) {
      mobileBtn.addEventListener('click', toggleMobileMenu);
    }
    
    // Close mobile menu when clicking a link
    document.querySelectorAll('.mobile-nav a, .mobile-submenu a').forEach(link => {
      link.addEventListener('click', closeMobileMenu);
    });
    
    // Cart drawer open/close
    if (cartBtn) cartBtn.addEventListener('click', openCartDrawer);
    if (closeCartBtn) closeCartBtn.addEventListener('click', closeCartDrawer);
    if (overlay) overlay.addEventListener('click', closeCartDrawer);
    if (checkoutBtn) checkoutBtn.addEventListener('click', proceedCheckout);
    
    // Close cart drawer on Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        closeCartDrawer();
      }
    });
    
    // Auto-sync cart every 30 seconds
    setInterval(loadShopifyCart, 30000);
    
    console.log('Sapuna Header initialized - Cart functions available globally');
  }
  
  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();